import PQueue from 'p-queue';
import React, {
  createContext,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

type LoadFn<TResponse = any, TParams = any> = (
  params: TParams
) => Promise<TResponse>;

interface ContextValue<TResponse = any, TParams = any> {
  /** Queue a request for loading */
  push: (params: TParams) => void;
  /** Loading state for all items in the queue */
  loading: string[];
  /** Object that stores the response of each request. Each key is the JSON-stringified `params` from `requestLoad` (should be unique) */
  data: Record<string, TResponse | undefined>;
  /** Fn that sets the data loader function */
  setLoader: (fn: LoadFn<TResponse>) => void;
}

const Context = createContext<ContextValue | null>(null);

interface ProviderProps {
  children: React.ReactNode;
  /** Max concurrent loads. Default 1 */
  concurrency?: number;
  /** Remember responses from previous fetches. Default true */
  cache?: boolean;
}

function Provider<TResponse = any, TParams = any>({
  children,
  concurrency = 1,
  cache = true,
}: ProviderProps) {
  const [loading, setLoading] = useState<string[]>([]);
  const [data, setData] = useState<Record<string, any>>({});
  const loaderFn = useRef<LoadFn<TResponse, TParams> | null>(null);
  const queue = useRef<PQueue>();

  // Initialize the promise queue
  useEffect(() => {
    queue.current = new PQueue({ concurrency });
    return () => {
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      queue.current?.clear();
    };
  }, [concurrency]);

  const push = useCallback(
    (params: TParams) => {
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (!queue.current || !loaderFn.current) return;

      const taskId = JSON.stringify(params);
      // Already loading/loaded
      if (loading.includes(taskId) || (data[taskId] && cache)) return;

      // Queue up the request
      setLoading((prev) => [...prev, taskId]);

      queue.current.add(async () => {
        try {
          const result = await loaderFn.current!(params);
          setData((prev) => ({ ...prev, [taskId]: result }));
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error(`Failed to load section ${taskId}`, err);
        } finally {
          setLoading((prev) => prev.filter((x) => x !== taskId));
        }
      });
    },
    [loading, data, cache]
  );

  const setLoader = useCallback((fn: LoadFn) => {
    loaderFn.current = fn;
  }, []);

  return (
    <Context.Provider value={{ push, loading, data, setLoader }}>
      {children}
    </Context.Provider>
  );
}

export { Context, Provider };
export type { ContextValue };
